import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Shield,
  CheckCircle,
  XCircle,
  Loader2,
  MapPin,
  Users,
  Car,
  Compass,
  Calendar,
  Sparkles,
  ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";
import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format } from "date-fns";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useSeo } from "@/hooks/useSeo";

declare global {
  interface Window {
    paypal?: any;
  }
}

interface TransferPaymentState {
  type?: "transfer";
  bookingId: string;
  bookingCode: string;
  from: string;
  to: string;
  passengers: number;
  routeType: string;
  totalPrice: number;
  pickupDate: string;
  returnDate: string | null;
  distanceKm: number | null;
  estimatedTimeMin: number | null;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  flightNumber: string | null;
  specialRequests: string | null;
  paymentMethod: string;
  vehicle: {
    name: string;
    image_url: string | null;
  };
}

interface TourPaymentState {
  type: "tour";
  bookingId: string;
  bookingCode: string;
  tourTitle: string;
  tourImage: string | null;
  destinationTitle: string;
  excursionDate: string;
  schedule: string | null;
  adults: number;
  children: number;
  pricingMode: string;
  selectedPackageName: string | null;
  totalPrice: number;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  pickupPoint: string;
  comments: string;
  paymentMethod: string;
}

type PaymentState = TransferPaymentState | TourPaymentState;
type PaymentStatus = "loading" | "ready" | "processing" | "success" | "cancelled" | "error";

const PaymentPage = () => {
  useSeo({ robots: "noindex,follow", title: "Payment | Allen Travels RD" });
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as PaymentState | null;

  const [status, setStatus] = useState<PaymentStatus>("loading");
  const [errorMsg, setErrorMsg] = useState("");
  const paypalContainerRef = useRef<HTMLDivElement>(null);
  const buttonsRendered = useRef(false);

  const isTour = state?.type === "tour";
  const totalPrice = state?.totalPrice ?? 0;

  useEffect(() => {
    if (!state) return;

    let scriptEl: HTMLScriptElement | null = null;

    const loadPayPal = async () => {
      const { data: pm } = await supabase
        .from("payment_methods")
        .select("client_id, environment")
        .eq("method", "paypal")
        .eq("is_active", true)
        .single();

      if (!pm?.client_id) {
        setStatus("error");
        setErrorMsg("PayPal is not configured. Please contact support.");
        return;
      }

      const existing = document.querySelector('script[src*="paypal.com/sdk/js"]');
      if (existing) existing.remove();

      scriptEl = document.createElement("script");
      scriptEl.src = `https://www.paypal.com/sdk/js?client-id=${pm.client_id}&currency=USD`;
      scriptEl.async = true;
      scriptEl.onload = () => {
        if (!buttonsRendered.current && window.paypal && paypalContainerRef.current) {
          buttonsRendered.current = true;
          renderButtons();
        }
      };
      scriptEl.onerror = () => {
        setStatus("error");
        setErrorMsg("Failed to load PayPal. Please try again.");
      };
      document.body.appendChild(scriptEl);
    };

    const renderButtons = () => {
      setStatus("ready");

      const description = isTour
        ? `Tour Booking ${state.bookingCode}`
        : `Transfer Booking ${(state as TransferPaymentState).bookingCode}`;

      window.paypal.Buttons({
        style: { layout: "vertical", shape: "rect", label: "pay" },
        createOrder: (_data: any, actions: any) => {
          return actions.order.create({
            purchase_units: [{
              description,
              amount: { value: totalPrice.toFixed(2), currency_code: "USD" },
            }],
          });
        },
        onApprove: async (_data: any, actions: any) => {
          setStatus("processing");
          try {
            const details = await actions.order.capture();
            const table = isTour ? "tour_bookings" : "bookings";

            const { error } = await supabase
              .from(table)
              .update({
                status: "confirmed",
                payment_status: "paid",
                stripe_payment_id: details.id,
              } as any)
              .eq("id", state.bookingId);

            if (error) {
              console.error(error);
              setStatus("error");
              setErrorMsg("Payment captured but failed to update booking. Please contact support.");
              return;
            }

            if (isTour) {
              supabase.functions.invoke("send-tour-booking-email", {
                body: { booking_id: state.bookingId },
              }).catch((err) => console.error("Tour email send error:", err));
            } else {
              supabase.functions.invoke("send-booking-email", {
                body: { booking_id: state.bookingId },
              }).catch((err) => console.error("Email send error:", err));
            }

            setStatus("success");
            toast.success("Payment successful!");

            setTimeout(() => {
              if (isTour) {
                const ts = state as TourPaymentState;
                navigate("/tour-confirmation", {
                  state: {
                    booking: {
                      id: ts.bookingId,
                      booking_code: ts.bookingCode,
                      tour_id: null,
                      excursion_date: ts.excursionDate,
                      schedule: ts.schedule,
                      adults: ts.adults,
                      children: ts.children,
                      pricing_mode: ts.pricingMode,
                      total_price: ts.totalPrice,
                      customer_name: ts.customerName,
                      customer_email: ts.customerEmail,
                      customer_phone: ts.customerPhone,
                      pickup_point: ts.pickupPoint,
                      comments: ts.comments,
                      payment_method: "paypal",
                      payment_status: "paid",
                      status: "confirmed",
                    },
                    tourTitle: ts.tourTitle,
                    tourImage: ts.tourImage,
                    destinationTitle: ts.destinationTitle,
                    duration: null,
                    pricingMode: ts.pricingMode,
                    selectedPackageName: ts.selectedPackageName,
                    adultPrice: null,
                    childPrice: null,
                  },
                });
              } else {
                const tf = state as TransferPaymentState;
                navigate("/confirmation", {
                  state: {
                    booking: {
                      id: tf.bookingId,
                      booking_code: tf.bookingCode,
                      pickup_location: tf.from,
                      dropoff_location: tf.to,
                      passengers: tf.passengers,
                      route_type: tf.routeType,
                      total_price: tf.totalPrice,
                      pickup_date: tf.pickupDate,
                      return_date: tf.returnDate,
                      distance_km: tf.distanceKm,
                      estimated_time_min: tf.estimatedTimeMin,
                      customer_name: tf.customerName,
                      customer_email: tf.customerEmail,
                      customer_phone: tf.customerPhone,
                      flight_number: tf.flightNumber,
                      special_requests: tf.specialRequests,
                      payment_method: "paypal",
                      payment_status: "paid",
                      status: "confirmed",
                      stripe_payment_id: details.id,
                    },
                    vehicle: tf.vehicle,
                  },
                });
              }
            }, 1500);
          } catch (err) {
            console.error(err);
            setStatus("error");
            setErrorMsg("An error occurred capturing the payment.");
          }
        },
        onCancel: () => setStatus("cancelled"),
        onError: (err: any) => {
          console.error("PayPal error:", err);
          setStatus("error");
          setErrorMsg("PayPal encountered an error. Please try again.");
        },
      }).render(paypalContainerRef.current);
    };

    loadPayPal();

    return () => {
      if (scriptEl) scriptEl.remove();
      buttonsRendered.current = false;
    };
  }, [state]);

  if (!state) {
    return (
      <div className="min-h-screen bg-gradient-soft flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="text-center space-y-5 max-w-md">
            <div className="w-16 h-16 rounded-2xl bg-gradient-ocean mx-auto flex items-center justify-center">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h1 className="font-display text-2xl font-semibold text-foreground">No payment in progress</h1>
            <Button size="lg" onClick={() => navigate("/")} className="bg-gradient-ocean">Go Home</Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-soft flex flex-col">
      <Navbar />

      {/* Hero strip */}
      <section className="relative pt-28 pb-10 overflow-hidden isolate">
        <div className="absolute inset-0 z-0 bg-gradient-hero" />
        <div className="absolute inset-0 z-0 opacity-30 mix-blend-overlay bg-[radial-gradient(ellipse_at_top,white,transparent_60%)]" />
        <div className="relative z-10 container mx-auto px-4 text-white">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-white transition-colors mb-5"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass-dark text-xs font-medium tracking-[0.22em] uppercase">
            <Sparkles className="w-3.5 h-3.5 text-accent" /> Secure Payment
          </span>
          <h1 className="font-display text-3xl md:text-5xl font-semibold mt-4 leading-tight">
            One last step — <span className="text-gradient-sunset">complete payment</span>
          </h1>
          <p className="mt-3 text-white/75 max-w-xl">
            Your booking is reserved. Pay securely with PayPal to confirm.
          </p>
        </div>
      </section>

      <div className="container mx-auto px-4 pb-20 -mt-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {/* LEFT: Payment */}
          <div className="lg:col-span-2 space-y-6">
            {status === "success" ? (
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="p-10 rounded-2xl glass shadow-elegant text-center space-y-4"
              >
                <div className="w-16 h-16 rounded-full bg-gradient-ocean mx-auto flex items-center justify-center shadow-glow">
                  <CheckCircle className="w-8 h-8 text-white" />
                </div>
                <h2 className="font-display text-2xl font-semibold text-foreground">Payment Successful!</h2>
                <p className="text-muted-foreground">Redirecting to your confirmation…</p>
              </motion.div>
            ) : status === "cancelled" ? (
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="p-10 rounded-2xl glass shadow-elegant text-center space-y-4"
              >
                <div className="w-16 h-16 rounded-full bg-accent/15 mx-auto flex items-center justify-center">
                  <XCircle className="w-8 h-8 text-accent" />
                </div>
                <h2 className="font-display text-2xl font-semibold text-foreground">Payment Cancelled</h2>
                <p className="text-muted-foreground">Your booking is still saved. You can try again.</p>
                <div className="flex gap-3 justify-center pt-2">
                  <Button onClick={() => window.location.reload()} className="bg-gradient-ocean shadow-glow">
                    Try Again
                  </Button>
                  <Button variant="outline" onClick={() => navigate("/")}>Go Home</Button>
                </div>
              </motion.div>
            ) : status === "error" ? (
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="p-10 rounded-2xl glass shadow-elegant text-center space-y-4"
              >
                <div className="w-16 h-16 rounded-full bg-destructive/15 mx-auto flex items-center justify-center">
                  <XCircle className="w-8 h-8 text-destructive" />
                </div>
                <h2 className="font-display text-2xl font-semibold text-foreground">Payment Error</h2>
                <p className="text-muted-foreground">{errorMsg}</p>
                <Button variant="outline" onClick={() => navigate("/")}>Go Home</Button>
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="p-7 rounded-2xl glass shadow-card space-y-6"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-sunset flex items-center justify-center shadow-glow">
                    <Shield className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="font-display text-xl font-semibold text-foreground">Pay with PayPal</h2>
                    <p className="text-xs text-muted-foreground">Use your PayPal balance, card, or bank</p>
                  </div>
                </div>

                {(status === "loading" || status === "processing") && (
                  <div className="flex items-center justify-center py-10">
                    <Loader2 className="w-7 h-7 animate-spin text-primary" />
                    <span className="ml-3 text-muted-foreground">
                      {status === "processing" ? "Processing payment…" : "Loading PayPal…"}
                    </span>
                  </div>
                )}

                <div
                  ref={paypalContainerRef}
                  className={status === "loading" || status === "processing" ? "hidden" : ""}
                />

                <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground pt-2 border-t border-border/60">
                  <ShieldCheck className="w-3.5 h-3.5 text-primary" />
                  Processed securely by PayPal — we never store your card details
                </div>
              </motion.div>
            )}
          </div>

          {/* RIGHT: Summary */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.5 }}
              className="sticky top-28 rounded-2xl overflow-hidden glass shadow-elegant"
            >
              {isTour && (state as TourPaymentState).tourImage && (
                <div className="relative h-40 overflow-hidden">
                  <img src={(state as TourPaymentState).tourImage!} alt="" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--hero-bg))]/85 to-transparent" />
                </div>
              )}
              {!isTour && (state as TransferPaymentState).vehicle.image_url && (
                <div className="relative h-40 overflow-hidden">
                  <img src={(state as TransferPaymentState).vehicle.image_url!} alt="" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--hero-bg))]/85 to-transparent" />
                </div>
              )}

              <div className="p-6 space-y-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-[0.18em] font-semibold">
                      Booking Code
                    </p>
                    <p className="font-display font-semibold text-foreground text-lg tracking-wider">
                      {state.bookingCode}
                    </p>
                  </div>
                  <Badge variant="secondary" className="text-xs">
                    {isTour
                      ? "Tour"
                      : (state as TransferPaymentState).routeType === "round-trip"
                      ? "Round Trip"
                      : "One Way"}
                  </Badge>
                </div>

                <div className="space-y-3 text-sm border-t border-border/60 pt-4">
                  {isTour ? (
                    <>
                      <SummaryRow
                        icon={<Compass className="w-4 h-4" />}
                        label="Tour"
                        value={(state as TourPaymentState).tourTitle}
                      />
                      <SummaryRow
                        icon={<Calendar className="w-4 h-4" />}
                        label="Date"
                        value={format(new Date((state as TourPaymentState).excursionDate), "PPP")}
                      />
                      <SummaryRow
                        icon={<Users className="w-4 h-4" />}
                        label="Guests"
                        value={`${(state as TourPaymentState).adults}A${
                          (state as TourPaymentState).children > 0
                            ? ` · ${(state as TourPaymentState).children}C`
                            : ""
                        }`}
                      />
                    </>
                  ) : (
                    <>
                      <SummaryRow
                        icon={<MapPin className="w-4 h-4 text-primary" />}
                        label="Pick-up"
                        value={(state as TransferPaymentState).from}
                      />
                      <SummaryRow
                        icon={<MapPin className="w-4 h-4 text-accent" />}
                        label="Drop-off"
                        value={(state as TransferPaymentState).to}
                      />
                      <SummaryRow
                        icon={<Users className="w-4 h-4" />}
                        label="Passengers"
                        value={(state as TransferPaymentState).passengers}
                      />
                      <SummaryRow
                        icon={<Car className="w-4 h-4" />}
                        label="Vehicle"
                        value={(state as TransferPaymentState).vehicle.name}
                      />
                    </>
                  )}
                </div>

                <div className="pt-5 border-t border-border/60">
                  <div className="flex items-end justify-between">
                    <p className="text-muted-foreground text-sm">Total</p>
                    <p className="text-3xl font-display font-semibold text-gradient-ocean">
                      ${totalPrice.toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

const SummaryRow = ({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}) => (
  <div className="flex items-start justify-between gap-3">
    <span className="text-muted-foreground flex items-center gap-1.5 shrink-0">
      {icon} {label}
    </span>
    <span className="font-medium text-foreground text-right truncate min-w-0">{value}</span>
  </div>
);

export default PaymentPage;
